import { z } from 'zod'
import {
  aufgabengroesseSchema,
  GROESSE_MINUTEN,
  OHNE_GROESSE_MINUTEN,
  taskPrioritySchema,
  ZEITFENSTER,
} from './status'

/**
 * **Planentwurf von Claude** ([Z-25](../../../docs/phasen/zusatz-25-claude-planung.md),
 * [E-458](../../../docs/ENTSCHEIDUNGEN.md#e-458)).
 *
 * Claude legt über MCP nichts im Bestand an, sondern einen *Entwurf*: eine flache Liste von
 * **Posten** (Vorhaben, Meilensteine, Aufgaben, Unteraufgaben), die über Schlüssel aufeinander
 * zeigen. Nils prüft ihn in Work, ändert, gibt Rückmeldung – und erst „Übernehmen" legt an.
 *
 * Schema und Prüfung stehen hier, weil beide Seiten sie brauchen: der Server (Antwort an Claude,
 * Übernehmen) und die Entwurfsseite (Warnungen beim Bearbeiten).
 */

export const PLAN_POSTEN_ARTEN = ['vorhaben', 'meilenstein', 'aufgabe', 'unteraufgabe'] as const
export type PlanPostenArt = (typeof PLAN_POSTEN_ARTEN)[number]

export const PLAN_ENTWURF_STAENDE = ['offen', 'teilweise', 'uebernommen', 'verworfen'] as const
export type PlanEntwurfStand = (typeof PLAN_ENTWURF_STAENDE)[number]

/** Ein Entwurf ist noch zu bearbeiten – offen oder mittendrin abgebrochen. */
export const planEntwurfOffen = (s: PlanEntwurfStand): boolean => s === 'offen' || s === 'teilweise'

export const PLAN_GRENZEN = { meilensteine: 20, aufgaben: 200, unteraufgaben: 400 } as const

const DATUM = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-TT')

export const planPostenSchema = z.object({
  /** Frei gewählt, eindeutig im Entwurf: `v`, `m1`, `a12`, `a12-1`. */
  schluessel: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,40}$/, 'Schlüssel: Buchstaben, Ziffern, - und _, höchstens 40'),
  art: z.enum(PLAN_POSTEN_ARTEN),
  /**
   * Wohin der Posten gehört: Aufgabe → Meilenstein (oder nichts = direkt ans Vorhaben),
   * Unteraufgabe → Aufgabe. Vorhaben und Meilensteine haben keine Eltern.
   */
  eltern: z.string().max(40).optional(),
  titel: z.string().trim().min(1).max(500),
  beschreibung: z.string().max(20_000).optional(),
  /** Vorhaben: Zieltermin · Meilenstein: Termin · Aufgabe: Frist. */
  termin: DATUM.optional(),
  zeitfenster: z.enum(ZEITFENSTER).optional(),
  groesse: aufgabengroesseSchema.optional(),
  prioritaet: taskPrioritySchema.optional(),
  /** Pool-Kennung aus der Welt des Entwurfs. Beim Vorhaben: sein Pool. */
  pool: z.string().max(32).optional(),
  /** Nur am Vorhaben. */
  typ: z.enum(['projekt', 'thema']).optional(),
  /** Bestehender Meilenstein des Ziel-Vorhabens – für Aufgaben, wenn der Entwurf ergänzt. */
  meilensteinId: z.string().max(40).optional(),
  /** Wunsch-Entwickler (Personen-Kennungen) – Pull statt Push. */
  wunsch: z.array(z.string().max(40)).max(5).optional(),
  /** Fester Bearbeiter – **setzt nur ein Mensch** im Entwurf, nie Claude. */
  bearbeiter: z.string().max(40).optional(),
  /** Das Häkchen auf der Entwurfsseite: wird übernommen. */
  an: z.boolean().default(true),
  /** Was beim Übernehmen entstand – gesetzt nur vom Server. */
  ergebnis: z.string().max(40).optional(),
})
export type PlanPosten = z.infer<typeof planPostenSchema>
export type PlanPostenEingabe = z.input<typeof planPostenSchema>

export const planPostenListeSchema = z.array(planPostenSchema).max(700)

export interface PlanBefund {
  /** `fehler` verhindert Speichern und Übernehmen, `warnung` nicht. */
  art: 'fehler' | 'warnung'
  schluessel?: string
  text: string
  /**
   * Nur der Server kann es wissen (Pools, Menschen, Prognose) – die Entwurfsseite zeigt diese
   * Befunde neben ihrer eigenen Prüfung beim Tippen.
   */
  nurServer?: boolean
}

export interface PlanUmfang {
  meilensteine: number
  aufgaben: number
  unteraufgaben: number
  /** Aufgaben je Größe; `ohne` = keine Größe angegeben. */
  groessen: { klein: number; mittel: number; gross: number; ohne: number }
  /** Grobe Summe in Minuten (Hausmaß je Größe). */
  minuten: number
}

export interface PlanPruefKontext {
  /** Heute als `JJJJ-MM-TT`. */
  heute: string
  /** Ergänzt der Entwurf ein bestehendes Vorhaben? Dann gibt es keinen Vorhaben-Posten. */
  zielVorhaben?: { zieltermin: string | null } | null
  /** Prognose des bestehenden Vorhabens, wenn es eine hat. */
  prognose?: { offen: number; brauchtSchwach: number; wochenBis: number } | null
}

/** `2026-12-04` → `4.12.2026` – für die Befunde, die Menschen lesen. */
const tt = (t: string | null | undefined): string => {
  if (!t) return ''
  const [j, m, d] = t.split('-')
  return `${Number(d)}.${Number(m)}.${j}`
}

/** Nur die Posten, die übernommen werden sollen. */
const aktiv = (p: PlanPosten) => p.an

/**
 * **Die Prüfung** – Aufbau (Fehler) und Plausibilität (Warnungen). Eine reine Funktion: dieselbe
 * Antwort für Claude nach `plan_entwerfen` und für die Entwurfsseite beim Tippen.
 */
export function planPruefen(
  posten: readonly PlanPosten[],
  kontext: PlanPruefKontext,
): { befunde: PlanBefund[]; umfang: PlanUmfang } {
  const befunde: PlanBefund[] = []
  const fehler = (text: string, schluessel?: string) =>
    befunde.push({ art: 'fehler', text, ...(schluessel ? { schluessel } : {}) })
  const warnung = (text: string, schluessel?: string) =>
    befunde.push({ art: 'warnung', text, ...(schluessel ? { schluessel } : {}) })

  const nachSchluessel = new Map<string, PlanPosten>()
  for (const p of posten) {
    if (nachSchluessel.has(p.schluessel))
      fehler(`Schlüssel „${p.schluessel}" doppelt`, p.schluessel)
    nachSchluessel.set(p.schluessel, p)
  }

  const vorhaben = posten.filter((p) => p.art === 'vorhaben')
  const meilensteine = posten.filter((p) => p.art === 'meilenstein')
  const aufgaben = posten.filter((p) => p.art === 'aufgabe')
  const unter = posten.filter((p) => p.art === 'unteraufgabe')

  if (kontext.zielVorhaben) {
    if (vorhaben.length > 0) {
      fehler('Der Entwurf ergänzt ein bestehendes Vorhaben – kein eigener Vorhaben-Posten')
    }
  } else if (vorhaben.length !== 1) {
    fehler(
      vorhaben.length === 0
        ? 'Ein neues Vorhaben braucht genau einen Posten der Art „vorhaben"'
        : 'Höchstens ein Vorhaben je Entwurf',
    )
  }
  if (meilensteine.length > PLAN_GRENZEN.meilensteine) {
    fehler(`Höchstens ${PLAN_GRENZEN.meilensteine} Meilensteine je Entwurf`)
  }
  if (aufgaben.length > PLAN_GRENZEN.aufgaben) {
    fehler(`Höchstens ${PLAN_GRENZEN.aufgaben} Aufgaben je Entwurf`)
  }
  if (unter.length > PLAN_GRENZEN.unteraufgaben) {
    fehler(`Höchstens ${PLAN_GRENZEN.unteraufgaben} Unteraufgaben je Entwurf`)
  }

  for (const p of posten) {
    const eltern = p.eltern ? nachSchluessel.get(p.eltern) : undefined
    if (p.eltern && !eltern)
      fehler(`„${p.titel}": Eltern „${p.eltern}" gibt es nicht`, p.schluessel)
    if ((p.art === 'vorhaben' || p.art === 'meilenstein') && p.eltern) {
      fehler(
        `„${p.titel}": ${p.art === 'vorhaben' ? 'ein Vorhaben' : 'ein Meilenstein'} hat keine Eltern`,
        p.schluessel,
      )
    }
    if (p.art === 'aufgabe' && eltern && eltern.art !== 'meilenstein') {
      fehler(
        `„${p.titel}": eine Aufgabe hängt an einem Meilenstein oder direkt am Vorhaben`,
        p.schluessel,
      )
    }
    if (p.art === 'aufgabe' && p.eltern && p.meilensteinId) {
      fehler(
        `„${p.titel}": Meilenstein aus dem Entwurf oder ein bestehender – nicht beides`,
        p.schluessel,
      )
    }
    if (p.art === 'unteraufgabe' && (!eltern || eltern.art !== 'aufgabe')) {
      fehler(`„${p.titel}": eine Unteraufgabe braucht eine Aufgabe als Eltern`, p.schluessel)
    }
    if (p.art !== 'unteraufgabe' && p.titel.trim().length < 3) {
      fehler('Ein Titel mit weniger als drei Zeichen sagt nichts', p.schluessel)
    }
    if (p.meilensteinId && !kontext.zielVorhaben) {
      fehler(
        `„${p.titel}": bestehende Meilensteine gibt es nur beim Ergänzen eines Vorhabens`,
        p.schluessel,
      )
    }
    if (p.art === 'unteraufgabe' && (p.termin || p.groesse || p.prioritaet || p.pool)) {
      warnung(
        `„${p.titel}": Unteraufgaben haben keine Frist, Größe, Priorität oder Pool – das fällt weg`,
        p.schluessel,
      )
    }
  }

  /* ── Plausibilität: nur, was übernommen wird ───────────────────────── */
  const zieltermin = kontext.zielVorhaben
    ? kontext.zielVorhaben.zieltermin
    : (vorhaben.find(aktiv)?.termin ?? null)

  for (const p of posten.filter(aktiv)) {
    if (p.termin && p.termin < kontext.heute && p.art !== 'vorhaben') {
      warnung(`„${p.titel}": ${tt(p.termin)} liegt in der Vergangenheit`, p.schluessel)
    }
    if (p.art === 'vorhaben' && p.termin && p.termin < kontext.heute) {
      warnung(`Zieltermin ${tt(p.termin)} liegt in der Vergangenheit`, p.schluessel)
    }
    if (p.art === 'meilenstein' && p.termin && zieltermin && p.termin > zieltermin) {
      warnung(
        `Meilenstein „${p.titel}" (${tt(p.termin)}) liegt nach dem Zieltermin ${tt(zieltermin)}`,
        p.schluessel,
      )
    }
    if (p.art === 'aufgabe') {
      const ms = p.eltern ? nachSchluessel.get(p.eltern) : undefined
      if (p.termin && ms?.termin && p.termin > ms.termin) {
        warnung(
          `„${p.titel}": Frist ${tt(p.termin)} nach dem Meilenstein „${ms.titel}" (${tt(ms.termin)})`,
          p.schluessel,
        )
      }
      if (p.termin && zieltermin && p.termin > zieltermin) {
        warnung(
          `„${p.titel}": Frist ${tt(p.termin)} nach dem Zieltermin ${tt(zieltermin)}`,
          p.schluessel,
        )
      }
      if (ms && !ms.an) {
        warnung(
          `„${p.titel}": ihr Meilenstein „${ms.titel}" wird nicht übernommen – sie hängt dann direkt am Vorhaben`,
          p.schluessel,
        )
      }
    }
    if (p.art === 'unteraufgabe') {
      const a = p.eltern ? nachSchluessel.get(p.eltern) : undefined
      if (a && !a.an && !a.ergebnis) {
        warnung(
          `„${p.titel}": ihre Aufgabe wird nicht übernommen – die Unteraufgabe dann auch nicht`,
          p.schluessel,
        )
      }
    }
  }

  const aktiveAufgaben = aufgaben.filter(aktiv)
  const groessen = { klein: 0, mittel: 0, gross: 0, ohne: 0 }
  for (const a of aktiveAufgaben) groessen[a.groesse ?? 'ohne'] += 1
  const minuten =
    groessen.klein * GROESSE_MINUTEN.klein +
    groessen.mittel * GROESSE_MINUTEN.mittel +
    groessen.gross * GROESSE_MINUTEN.gross +
    groessen.ohne * OHNE_GROESSE_MINUTEN

  if (groessen.ohne > 0 && aktiveAufgaben.length > 0) {
    warnung(`${groessen.ohne} von ${aktiveAufgaben.length} Aufgaben ohne Größe`)
  }

  /**
   * **Gegen die Prognose**: hat das Ziel-Vorhaben eine, wächst der Bedarf mit den neuen Aufgaben
   * im selben Verhältnis – grob, aber es sagt, ob der Plan den Termin reißt.
   */
  const p = kontext.prognose
  if (p && p.offen > 0 && Number.isFinite(p.brauchtSchwach) && aktiveAufgaben.length > 0) {
    const braucht = (p.brauchtSchwach * (p.offen + aktiveAufgaben.length)) / p.offen
    if (braucht > p.wochenBis) {
      warnung(
        `Mit ${aktiveAufgaben.length} neuen Aufgaben braucht das Vorhaben in schwachen Wochen etwa ${Math.ceil(braucht)} Wochen – bis zum Zieltermin sind es ${Math.round(p.wochenBis)}`,
      )
    }
  }

  return {
    befunde,
    umfang: {
      meilensteine: meilensteine.filter(aktiv).length,
      aufgaben: aktiveAufgaben.length,
      unteraufgaben: unter.filter(aktiv).length,
      groessen,
      minuten,
    },
  }
}

/**
 * **Was ein Mensch seit der letzten Fassung von Claude geändert hat** – für `entwurf_lesen`, damit
 * Claude beim Überarbeiten weiß, was Nils schon entschieden hat.
 */
export function planUnterschied(
  vorher: readonly PlanPosten[],
  nachher: readonly PlanPosten[],
): { neu: string[]; weg: string[]; geaendert: { schluessel: string; felder: string[] }[] } {
  const alt = new Map(vorher.map((p) => [p.schluessel, p]))
  const jetzt = new Map(nachher.map((p) => [p.schluessel, p]))
  const geaendert: { schluessel: string; felder: string[] }[] = []
  for (const [k, p] of jetzt) {
    const a = alt.get(k)
    if (!a) continue
    const felder = (Object.keys({ ...a, ...p }) as (keyof PlanPosten)[]).filter(
      (f) => f !== 'ergebnis' && JSON.stringify(a[f]) !== JSON.stringify(p[f]),
    )
    if (felder.length) geaendert.push({ schluessel: k, felder })
  }
  return {
    neu: [...jetzt.keys()].filter((k) => !alt.has(k)),
    weg: [...alt.keys()].filter((k) => !jetzt.has(k)),
    geaendert,
  }
}

/**
 * Beim Überarbeiten bleiben **angelegte Posten** wie sie sind: ihr Ergebnis wird übernommen, und
 * fehlen darf keiner – das Angelegte steht schon im Bestand.
 */
export function planErgebnisseBehalten(
  bisher: readonly PlanPosten[],
  neu: readonly PlanPosten[],
): PlanPosten[] {
  const angelegt = new Map(bisher.filter((p) => p.ergebnis).map((p) => [p.schluessel, p]))
  const fehlend = [...angelegt.keys()].filter((k) => !neu.some((p) => p.schluessel === k))
  if (fehlend.length) {
    throw new Error(
      `Schon angelegt und deshalb nicht wegzulassen: ${fehlend.join(', ')} – diese Posten unverändert mitschicken`,
    )
  }
  return neu.map((p) => {
    const a = angelegt.get(p.schluessel)
    return a ? { ...a } : { ...p, ergebnis: undefined }
  })
}
